import * as THREE from 'three';
import { resolveCameraViewPose } from '../../runtime/_pure/cameraNavigation.js';
import { resolveCameraPose } from '../../runtime/_pure/cameraPose.js';
/** Where a viewer looking into a portal is assumed to stand, in front of its centre. */
export const PORTAL_PREVIEW_VIEW_DISTANCE = 2.5;
/** Portals farther than this from the viewer are not previewed. */
export const PORTAL_FOCUS_RADIUS = 8;
/** A closer portal takes focus only when it is closer by this much, so focus does not flicker between neighbours. */
export const PORTAL_FOCUS_HYSTERESIS = 0.5;
const UP = new THREE.Vector3(0, 1, 0);
function pose(eye, target) {
    const rotation = new THREE.Matrix4().lookAt(eye, target, UP);
    return rotation.setPosition(eye);
}
/**
 * Maps a space into the world behind a portal so that its authored camera framing fills the opening
 * for a viewer standing PORTAL_PREVIEW_VIEW_DISTANCE in front of it. Like XR arrival, the eye moves
 * toward the target until the authored field of view matches the opening's.
 */
export function resolvePortalPreviewMatrix(portal, cameraPose, viewDistance = PORTAL_PREVIEW_VIEW_DISTANCE) {
    const target = new THREE.Vector3(...cameraPose.target);
    const direction = target.clone().sub(new THREE.Vector3(...cameraPose.position));
    const openingFov = 2 * Math.atan(portal.height / 2 / viewDistance);
    const scale = Math.tan(THREE.MathUtils.degToRad(cameraPose.fov) / 2) / Math.tan(openingFov / 2);
    const sourceEye = target.clone().addScaledVector(direction, -scale);
    const center = new THREE.Vector3(...portal.center);
    const viewerEye = center.clone().addScaledVector(new THREE.Vector3(...portal.facing), viewDistance);
    return pose(viewerEye, center).multiply(pose(sourceEye, target).invert());
}
/**
 * Guided views fit their content to a viewport. Resolving them for a wide one, like desktop and the
 * portal snapshots (a centre crop of 1280x800), keeps a narrow opening from pushing the camera far back.
 */
export const PORTAL_PREVIEW_SOURCE_ASPECT = 16 / 10;
/**
 * The camera pose a viewer arriving in the frame settles at: a guided active view when the space
 * declares one, otherwise the authored pose.
 */
export function resolvePortalPreviewPose(frame) {
    const pose = resolveCameraPose(frame.spaceRenderOptions);
    const camera = frame.spaceRenderOptions?.camera;
    const view = camera?.navigationMode === 'guided' && camera.activeView !== undefined
        ? camera.graph?.views[camera.activeView] : undefined;
    if (!view)
        return pose;
    try {
        return resolveCameraViewPose(frame, view, pose, PORTAL_PREVIEW_SOURCE_ASPECT);
    }
    catch {
        return pose;
    }
}
function frontDistance(portal, eye) {
    const dx = eye[0] - portal.center[0];
    const dz = eye[2] - portal.center[2];
    if (dx * portal.facing[0] + dz * portal.facing[2] <= 0)
        return undefined;
    const distance = Math.hypot(dx, dz);
    return distance <= PORTAL_FOCUS_RADIUS ? distance : undefined;
}
/** The nearest portal the viewer stands in front of, keeping the current one unless another is clearly closer. */
export function selectFocusedPortal(portals, eye, currentKey) {
    let best;
    let bestDistance = Infinity;
    let current;
    let currentDistance = Infinity;
    for (const portal of portals) {
        const distance = frontDistance(portal, eye);
        if (distance === undefined)
            continue;
        if (portal.portalKey === currentKey) {
            current = portal;
            currentDistance = distance;
        }
        if (distance < bestDistance) {
            best = portal;
            bestDistance = distance;
        }
    }
    return current && currentDistance <= bestDistance + PORTAL_FOCUS_HYSTERESIS ? current : best;
}
